import React, { useEffect, useRef, useState } from 'react';
import { useP } from '../i18n/useP';

/**
 * Cellule texte ou nombre éditable en inline.
 * • 1 clic → input ouvert
 * • select-all automatique → on tape directement par-dessus (écrase le 0)
 * • type="text" + inputMode → pas de flèches/spinner
 * • virgule acceptée comme séparateur décimal
 * • Enter/blur → onSave({ field: valeur }) ; Escape → annule
 */
export function EditableCell({ value, field, type = 'text', placeholder = null, onSave }) {
  const p = useP();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft]     = useState('');
  const ref = useRef();

  // Sélectionner tout dès l'ouverture → on tape par-dessus directement
  useEffect(() => {
    if (editing && ref.current) {
      ref.current.focus();
      ref.current.select();
    }
  }, [editing]);

  function start() {
    setDraft(type === 'number' ? String(value ?? 0) : String(value ?? ''));
    setEditing(true);
  }

  function commit() {
    setEditing(false);
    const parsed = type === 'number'
      ? (parseFloat((draft + '').replace(/[\s']/g, '').replace(',', '.')) || 0)
      : draft.trim();
    if (String(parsed) !== String(value ?? '')) onSave({ [field]: parsed });
  }

  function onKey(e) {
    if (e.key === 'Enter') { e.preventDefault(); commit(); }
    if (e.key === 'Escape') setEditing(false);
  }

  if (editing) {
    return (
      <input
        ref={ref}
        className="p-cell-input"
        type="text"
        inputMode={type === 'number' ? 'decimal' : 'text'}
        value={draft}
        onChange={e => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={onKey}
      />
    );
  }

  const display = (value !== null && value !== undefined && value !== '')
    ? (type === 'number'
        ? Number(value).toLocaleString('de-CH', { maximumFractionDigits: 2 })
        : value)
    : null;

  /* Un tiret ne dit pas qu'on PEUT écrire. Quand la cellule vide invite à une
     saisie libre,  l'annonce — en bleu atténué : la couleur du
     champ à saisir, l'intensité de ce qui n'est pas encore une donnée. */
  return (
    <span className="p-cell-text" onClick={start} title={p('Cliquer pour modifier')}>
      {display ?? (placeholder
        ? <em className="p-cell-ph">{placeholder}</em>
        : <em className="p-cell-empty">—</em>)}
    </span>
  );
}

/**
 * Cellule select éditable en inline.
 */
export function SelectCell({ value, field, options, onSave }) {
  const p = useP();
  const [editing, setEditing] = useState(false);
  if (editing) {
    return (
      <select
        className="p-cell-input"
        value={value}
        autoFocus
        onChange={e => { onSave({ [field]: e.target.value }); setEditing(false); }}
        onBlur={() => setEditing(false)}
      >
        {options.map(o => <option key={o.value ?? o} value={o.value ?? o}>{o.label ?? o}</option>)}
      </select>
    );
  }
  const label = options.find(o => (o.value ?? o) === value)?.label ?? value;
  return (
    <span className="p-cell-text" onClick={() => setEditing(true)} title={p('Cliquer pour modifier')}>
      {label || <em className="p-cell-empty">—</em>}
    </span>
  );
}
